import Config from './Config';
import UserAgentDetector from '../utils/UserAgentDetector';
import VirtualKeyboardManager from './VirtualKeyboardManager';

export default new class ScaleManager {
  constructor() {
    window.addEventListener('visibilitychange', this.requestWakeLock.bind(this));
    this.requestWakeLock();

    this._defaultFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
    this._currentFontSize = this._defaultFontSize;
    document.documentElement.style.setProperty(`--default-font-size`, this._defaultFontSize);
    document.documentElement.style.setProperty(`--font-size`, this._currentFontSize);

    this.onResize = this.onResize.bind(this);

    this._isResizeListenersAttached = false;
    this._canvasUpdatedInitially = false;
    this.initialized = false;
  }

  preInit(data, config) { //Init without canvas
    this.init(data, config, true);
  }

  init(data, config, isPreInit) {
    if (this.initialized) return;
    this.initialized = !isPreInit;
    this.config = config ? { ...Config, ...config } : Config;
    this.currentSizes = null;
    this._currentData = null;
    this.gameContainer = window.OPWrapperService.initConfig.gameContainer;

    this.data = data || null;
    this.addListeners();
    this.onResize(true);
  }

  addListeners() {
    if (this._isResizeListenersAttached) return;
    this._isResizeListenersAttached = true;

    const listener = this.onResize.bind(this);
    visualViewport.addEventListener('resize', listener);

    if (screen.orientation && UserAgentDetector.isMobile.any) screen.orientation.addEventListener('change', () => {
      this._orientationType = screen.orientation.type;
      this.onResize(true);
      if (!UserAgentDetector.isIOSSafari) this._orientationType = null;
    });

    document.addEventListener('fullscreenchange', listener);
    /* Firefox */
    document.addEventListener('mozfullscreenchange', listener);
    /* Chrome, Safari and Opera */
    document.addEventListener('webkitfullscreenchange', listener);
    /* IE / Edge */
    document.addEventListener('msfullscreenchange', listener);
  }

  onResize(isForce) {
    console.log('Is virtual keyboard affects layout: ', VirtualKeyboardManager.isVirtualKeyboardAffectsLayout);

    let newInnerWidth = window.innerWidth;
    let newInnerHeight = window.innerHeight;

    if (
      (this.innerWidth === newInnerWidth
        && this.innerHeight === newInnerHeight
        && isForce !== true
      ) ||
      (this._canvasUpdatedInitially && VirtualKeyboardManager.isVirtualKeyboardAffectsLayout)
      // (this._canvasUpdatedInitially && Fullscreen.isDrag)
    ) return;

    this.innerWidth = newInnerWidth;
    this.innerHeight = newInnerHeight;

    this.checkOrientation();
    this.calculateCurrentSize();

    const scaleRatio = (this.data && this.data.scaleRatio) || 1; //UserAgentDetector.isMobile.any ? 2 : 1;

    const finalInnerWidth = this.innerWidth * scaleRatio;
    const finalInnerHeight = this.innerHeight * scaleRatio;

    this._currentScale = Math.min(finalInnerWidth / this.safeZone.width, finalInnerHeight / this.safeZone.height);
    const uiScale = Math.min(finalInnerWidth / this.safeZone.width * this._uiScaleRelativeToCanvasX, (finalInnerHeight) / this.safeZone.height * this._uiScaleRelativeToCanvasY);
    this._uiScaleRelativeToCanvasScale = uiScale / this._currentScale;
    this._currentFontSize = this._defaultFontSize * uiScale;
    this.updateGlobalVariables();

    this._currentData = {
      orientation: this.orientation,
      isPortrait: this.orientation === 'V',
      isLandscape: this.orientation === 'H',

      gameWidth: this.currentSizes.width,
      gameHeight: this.currentSizes.height,
      stageX: (finalInnerWidth - (this.safeZone.width * this._currentScale)) / 2 - (this.safeZone.left) * this._currentScale,
      stageY: this.config.BIND_TO_TOP ? 0 : (finalInnerHeight - (this.safeZone.height * this._currentScale)) / 2 - this.safeZone.top * this._currentScale,

      isMobile: UserAgentDetector.isMobile.any,
      scaleWidth: finalInnerWidth / this._currentScale / this.currentSizes.width,
      scaleHeight: finalInnerHeight / this._currentScale / this.currentSizes.height,
      innerWidth: this.innerWidth,
      innerHeight: this.innerHeight,
      finalInnerWidth: finalInnerWidth,
      finalInnerHeight: finalInnerHeight,
      currentFontSize: this._currentFontSize,
      scale: this._currentScale,
      uiScale: uiScale,
      uiScaleRelativeToCanvasScale: this._uiScaleRelativeToCanvasScale,
    };

    if (this.initialized) this.updateCanvas();
    if (process?.env?.NODE_ENV === 'development') console.log('SCALE MANAGER RESIZE')
    this.emitResize();
  }

  updateCanvas() {
    this._canvasUpdatedInitially = true;
    this.data.app.view.style.width = `${this.currentData.innerWidth}px`;
    this.data.app.view.style.height = `${this.currentData.innerHeight}px`;
    this.data.app.renderer.resize(this.currentData.finalInnerWidth, this.currentData.finalInnerHeight);
    this.data.app.stage.scale.set(this.currentData.scale, this.currentData.scale);
    this.data.app.stage.x = this.currentData.stageX;
    this.data.app.stage.y = this.currentData.stageY;

    this._currentData = {
      ...this._currentData,
      canvasWidth: this.data.app.view.width,
      canvasHeight: this.data.app.view.height,
      stageHeight: this.data.app.stage.height,
      stageWidth: this.data.app.stage.width,
    }
  }

  emitResize() {
    window.OPWrapperService.eventManager.dispatch(window.OPWrapperService.eventManager.types.EET_SCALE_MANAGER_RESIZE, this.currentData);
  }

  checkOrientation() {
    if (this.config.FORCE_PORTRAIT || (UserAgentDetector.isMobile.any && (this._orientationType ? /portrait/.test(this._orientationType) : this.innerWidth < this.innerHeight))) {
      this.orientation = 'V';
      this.shiftX = (this.config.CANVAS_WIDTH_PORTRAIT - this.config.SAFE_WIDTH_PORTRAIT) / 2;
      this.shiftY = this.config.BIND_TO_TOP ? 0 : (this.config.CANVAS_HEIGHT_PORTRAIT - this.config.SAFE_HEIGHT_PORTRAIT) / 2;
      this.safeZone = {
        left: this.shiftX,
        top: this.shiftY,
        width: this.config.SAFE_WIDTH_PORTRAIT,
        height: this.config.SAFE_HEIGHT_PORTRAIT
      };
      this._uiScaleRelativeToCanvasX = this.config.CANVAS_WIDTH_PORTRAIT / this.config.CANVAS_WIDTH_FOR_UI_PORTRAIT;
      this._uiScaleRelativeToCanvasY = this.config.CANVAS_HEIGHT_PORTRAIT / this.config.CANVAS_HEIGHT_FOR_UI_PORTRAIT;

      this.gameRatio = this.config.CANVAS_WIDTH_PORTRAIT / this.config.CANVAS_HEIGHT_PORTRAIT;
      window.OPWrapperService.ControllerStatistic.setOrientation('portrait');
    } else {
      this.orientation = 'H';
      this.shiftX = (this.config.CANVAS_WIDTH_LANDSCAPE - this.config.SAFE_WIDTH_LANDSCAPE) / 2;
      this.shiftY = this.config.BIND_TO_TOP ? 0 : (this.config.CANVAS_HEIGHT_LANDSCAPE - this.config.SAFE_HEIGHT_LANDSCAPE) / 2;
      this.safeZone = {
        left: this.shiftX,
        top: this.shiftY,
        width: this.config.SAFE_WIDTH_LANDSCAPE,
        height: this.config.SAFE_HEIGHT_LANDSCAPE
      };
      this._uiScaleRelativeToCanvasX = this.config.CANVAS_WIDTH_LANDSCAPE / this.config.CANVAS_WIDTH_FOR_UI_LANDSCAPE;
      this._uiScaleRelativeToCanvasY = this.config.CANVAS_HEIGHT_LANDSCAPE / this.config.CANVAS_HEIGHT_FOR_UI_LANDSCAPE;

      this.gameRatio = this.config.CANVAS_WIDTH_LANDSCAPE / this.config.CANVAS_HEIGHT_LANDSCAPE;
      window.OPWrapperService.ControllerStatistic.setOrientation('landscape');
    }
  }

  calculateCurrentSize() {
    this.currentSizes = UserAgentDetector.isMobile.any ? (this.orientation === 'H' ? this.data.gameSize.mobileLandscape : this.data.gameSize.mobilePortrait) : this.data.gameSize.desktop;
    if (!this.currentSizes) {
      this.currentSizes = this.data.gameSize;
    }
  }

  updateGlobalVariables() {
    document.documentElement.style.fontSize = this._currentFontSize + 'px';
    document.documentElement.style.setProperty(`--font-size`, this._currentFontSize);

    document.documentElement.style.setProperty(`--scale--game--width`, this.currentSizes.width / this._defaultFontSize);
    document.documentElement.style.setProperty(`--scale--game--height`, this.currentSizes.height / this._defaultFontSize);
    document.documentElement.style.setProperty(`--scale--safe-zone--height`, this.safeZone.height / this._defaultFontSize);
    document.documentElement.style.setProperty(`--scale--safe-zone--width`, this.safeZone.width / this._defaultFontSize);
    document.documentElement.style.setProperty(`--scale--safe-zone--top`, this.safeZone.top / this._defaultFontSize);
    document.documentElement.style.setProperty(`--scale--safe-zone--left`, this.safeZone.left / this._defaultFontSize);
  }

  async requestWakeLock() {
    if (document.visibilityState === 'visible' && 'wakeLock' in navigator) {
      try {
        await navigator.wakeLock.request('screen');
      } catch (e) {

      }
    }
  }

  convertPxToRem(px) {
    return px / this._defaultFontSize;
  }

  get currentData() {
    return this._currentData;
  }

  get defaultFontSize() {
    return this._defaultFontSize;
  }

  get currentFontSize() {
    return this._currentFontSize;
  }

  get currentScale() {
    return this._currentScale;
  }

  get currentScaleCoefficient() {
    return this._defaultFontSize / this._currentFontSize;
  }
}
